import React, { useState } from 'react';
import '../App.css';
import '../assets/Drivers.css';
import { DriverCard } from '../components/DriverCard';
import { AddDriverModal } from '../components/AddDriverModal';
import { driversApi, ApiError } from '../utils/api';
import logoIcon from '../assets/logo/icon.svg';
import { useData } from '../context/DataContext';

const RefreshIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 4v6h-6" /><path d="M1 20v-6h6" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export const Drivers: React.FC = () => {
  const { drivers, driversLoading: loading, driversError: error, refreshDrivers } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    await refreshDrivers();
    setRefreshing(false);
  };

  const run = async (fn: () => Promise<any>, fallback: string) => {
    setActionError(null);
    try {
      await fn();
      await refreshDrivers();
      return true;
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : fallback);
      return false;
    }
  };

  const handleAddDriver = async (name: string, phone: string) => {
    if (await run(() => driversApi.create(name, phone), 'Network error: unable to add driver')) {
      setIsModalOpen(false);
    }
  };

  const handleDeleteDriver = (phone: string) =>
    run(() => driversApi.delete(phone), 'Network error: unable to delete driver');

  return (
    <div className="page">
      <header className="page__head">
        <img src={logoIcon} alt="" className="page__logo"
             onError={e => { e.currentTarget.style.display = 'none'; }} />
        <div className="page__titles">
          <h1 className="page__title">Drivers</h1>
          <p className="page__sub">{drivers.length} on the roster</p>
        </div>
        <div className="page__actions">
          <button className="btn btn--primary btn--sm hide-on-phone" onClick={() => setIsModalOpen(true)}>
            Add driver
          </button>
          <button
            className={`icon-btn ${refreshing || loading ? 'is-spinning' : ''}`}
            onClick={handleManualRefresh}
            disabled={refreshing || loading}
            aria-label="Refresh drivers"
          >
            <RefreshIcon />
          </button>
        </div>
      </header>

      {actionError && <div className="banner banner--danger">{actionError}</div>}

      {loading && drivers.length === 0 ? (
        <div className="dlist">
          {[0, 1, 2].map(i => <div key={i} className="skeleton" style={{ height: 66 }} />)}
        </div>
      ) : error ? (
        <div className="state state--error">
          <p className="state__title">Couldn’t load drivers</p>
          <p className="state__body">{error}</p>
          <button onClick={handleManualRefresh} className="btn btn--secondary">Try again</button>
        </div>
      ) : drivers.length === 0 ? (
        <div className="state">
          <p className="state__title">No drivers yet</p>
          <p className="state__body">Add a driver to start assigning optimized routes.</p>
          <button className="btn btn--primary" onClick={() => setIsModalOpen(true)}>Add driver</button>
        </div>
      ) : (
        <div className="dlist">
          {drivers.map((driver, i) => (
            <DriverCard
              key={driver.phone || i}
              driverId={driver.id || ''}
              driverName={driver.name}
              driverNumber={driver.phone}
              onDelete={() => handleDeleteDriver(driver.phone)}
            />
          ))}
        </div>
      )}

      <button className="fab" onClick={() => setIsModalOpen(true)} aria-label="Add driver">
        <PlusIcon />
      </button>

      <AddDriverModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddDriver}
      />
    </div>
  );
};
